import type { ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';

import { SiteFooter, SiteNav } from '../../components/SiteChrome';

/** Last substantive revision. Bump when the terms actually change. */
export const EFFECTIVE_DATE = '14 July 2026';

/** Where users reach a human. */
export const CONTACT_EMAIL = 'support@spitballai.com';

export function LegalPage({
  title,
  description,
  effectiveDate = EFFECTIVE_DATE,
  children,
}: {
  title: string;
  description: string;
  /** Overrides the shared date when one document is revised and the other isn't. */
  effectiveDate?: string;
  children: ReactNode;
}) {
  return (
    <>
      <Helmet>
        <title>{title} — ClientBridge</title>
        <meta name="description" content={description} />
      </Helmet>

      <SiteNav />

      <main className="mx-auto max-w-[760px] px-6 pb-20 pt-8 md:px-10">
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-warm-faint">
          Effective {effectiveDate}
        </span>
        <h1 className="mt-3.5 font-display text-[38px] font-bold leading-[1.05] tracking-[-0.02em] text-forest-deep md:text-[46px]">
          {title}
        </h1>

        <div className="legal mt-10">{children}</div>
      </main>

      <SiteFooter />
    </>
  );
}

export function Section({
  heading,
  children,
}: {
  heading: string;
  children: ReactNode;
}) {
  return (
    <section className="mt-9 first:mt-0">
      <h2 className="font-display text-[22px] font-bold leading-[1.2] tracking-[-0.01em] text-forest-deep">
        {heading}
      </h2>
      <div className="mt-3 space-y-3 font-body text-[15.5px] leading-[1.65] text-forest-muted [&_a]:text-orange [&_a:hover]:text-orange-dark [&_strong]:font-semibold [&_strong]:text-forest">
        {children}
      </div>
    </section>
  );
}

export function List({ items }: { items: ReactNode[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3">
          <span className="mt-[9px] h-1 w-1 flex-none rounded-full bg-orange" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}
